import React, { Component } from "react";
import PropTypes from "prop-types";
import { format } from "d3-format";
import displayValuesFor from "./displayValuesFor";
import GenericChartComponent from "../GenericChartComponent";

import { isDefined, functor, getMALabel } from "../utils";
import ToolTipText from "./ToolTipText";
import ToolTipTSpanLabel from "./ToolTipTSpanLabel";

// Canvas-based text measurement utility
let measureCanvas;
function getTextWidth(text, fontFamily = "Arial", fontSize = 12) {
  if (!measureCanvas) {
    measureCanvas = document.createElement("canvas");
  }
  const context = measureCanvas.getContext("2d");
  context.font = `${fontSize}px ${fontFamily}`;
  const metrics = context.measureText(text);
  return metrics.width;
}

const TOOLTIP_BG_HEIGHT = 18;
const TOOLTIP_BG_PADDING = 6;

class BollingerBandTooltip extends Component {
  constructor(props) {
    super(props);
    this.renderSVG = this.renderSVG.bind(this);
  }
  renderSVG(moreProps) {
    const {
      onClick,
      displayFormat,
      yAccessor,
      options,
      textFill,
      labelFill,
      bgFill,
      fontFamily,
      fontSize,
    } = this.props;
    const { displayValuesFor } = this.props;

    const {
      chartConfig: { width, height },
    } = moreProps;

    const currentItem = displayValuesFor(this.props, moreProps);

    let top, middle, bottom;
    top = middle = bottom = "n/a";

    if (isDefined(currentItem) && isDefined(yAccessor(currentItem))) {
      const item = yAccessor(currentItem);
      top = displayFormat(item.top);
      middle = displayFormat(item.middle);
      bottom = displayFormat(item.bottom);
    }

    const { origin: originProp } = this.props;
    const origin = functor(originProp);
    const [x, y] = origin(width, height);

    const { sourcePath, windowSize, multiplier, movingAverageType } = options;
    // Get MA type label from centralized utility
    const maLabel = getMALabel(movingAverageType);
    const tooltipLabel = `BB(${sourcePath}, ${windowSize}, ${multiplier}, ${maLabel}): `;
    const tooltipValue = `${top}, ${middle}, ${bottom}`;

    // Calculate text width for background
    const fullText = `${tooltipLabel}${tooltipValue}`;
    const textWidth = getTextWidth(
      fullText,
      fontFamily || "Arial",
      fontSize || 12,
    );
    const bgWidth = textWidth + TOOLTIP_BG_PADDING * 2;

    return (
      <g
        transform={`translate(${x}, ${y})`}
        className={this.props.className}
        onClick={onClick}
        data-evo-id="tooltip-bollinger-band"
      >
        {/* Background rectangle */}
        <rect
          x={-TOOLTIP_BG_PADDING}
          y={-5}
          width={bgWidth}
          height={TOOLTIP_BG_HEIGHT}
          fill={bgFill || "#1f2937"}
          rx={6}
          ry={6}
        />
        <ToolTipText x={0} y={0} fontFamily={fontFamily} fontSize={fontSize}>
          <ToolTipTSpanLabel fill={labelFill}>{tooltipLabel}</ToolTipTSpanLabel>
          <tspan fill={textFill}>{tooltipValue}</tspan>
        </ToolTipText>
      </g>
    );
  }
  render() {
    return (
      <GenericChartComponent
        clip={false}
        svgDraw={this.renderSVG}
        drawOn={["mousemove"]}
      />
    );
  }
}

BollingerBandTooltip.propTypes = {
  className: PropTypes.string,
  yAccessor: PropTypes.func.isRequired,
  displayValuesFor: PropTypes.func,
  displayFormat: PropTypes.func.isRequired,
  origin: PropTypes.array.isRequired,
  onClick: PropTypes.func,
  options: PropTypes.shape({
    sourcePath: PropTypes.string.isRequired,
    windowSize: PropTypes.number.isRequired,
    multiplier: PropTypes.number.isRequired,
    movingAverageType: PropTypes.string.isRequired,
  }).isRequired,
  textFill: PropTypes.string,
  labelFill: PropTypes.string,
  bgFill: PropTypes.string,
  fontFamily: PropTypes.string,
  fontSize: PropTypes.number,
};

BollingerBandTooltip.defaultProps = {
  className:
    "react-stockcharts-tooltip react-stockcharts-bollingerband-tooltip",
  displayFormat: format(".2f"),
  displayValuesFor: displayValuesFor,
  origin: [0, 10],
};

export default BollingerBandTooltip;
